import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { PageWidth } from "./components/travel-ui";
import { useMemberAuth } from "./auth/MemberAuthContext";
import { useWallet } from "./services/wallet.service";
import { MemberPortalApi } from "@/api/perkpoint/memberPortal";

const FAQS = [
  {
    q: "How do cash and points work together?",
    a: "At checkout you choose how many points to apply. Each point is worth the amount set on your program, and points can cover up to half the fare. You pay the rest in cash.",
  },
  {
    q: "What moves me to the next tier?",
    a: "Eligible cash spend on confirmed stays and flights counts. Points you redeem are not added to that spend.",
  },
  {
    q: "When do I earn points?",
    a: "Points are added when you confirm a booking. If your program holds them until after checkout, they show as pending and are not spendable until that date. Your available balance is what you can use at checkout.",
  },
  {
    q: "Can I cancel?",
    a: "Upcoming bookings you make here can be cancelled from Trips. Points and cash spend from that booking are reversed.",
  },
];

export default function MemberRewards() {
  const { session } = useMemberAuth();
  const wallet = useWallet();
  const name = wallet.fullName || session?.fullName || "Member";
  const [tiers, setTiers] = useState<{ name: string; benefits: string[] }[]>([]);
  useEffect(() => {
    void MemberPortalApi.tiers()
      .then((rows) => setTiers(rows.map((row) => ({ name: row.name, benefits: row.benefits || [] }))))
      .catch(() => setTiers([]));
  }, []);
  const shownTiers = tiers;
  const money = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: wallet.currency || "AED",
      maximumFractionDigits: wallet.pointValue < 1 ? 2 : 0,
    }).format(amount);

  return (
    <PageWidth>
      <h1 className="text-2xl font-bold">Rewards</h1>
      <p className="mt-1 text-sm text-[#595959]">
        Points, your tier, and what the next tier unlocks.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-[340px_1fr]">
        <div className="space-y-4">
          <section className="overflow-hidden rounded-2xl bg-[#003580] p-5 text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/70">
                  Perk Point
                </p>
                <p className="mt-2 text-3xl font-bold">{wallet.tierName}</p>
              </div>
              <span className="rounded-full bg-[#febb02] px-3 py-1 text-xs font-bold text-[#1a1a1a]">
                Active
              </span>
            </div>
            <p className="mt-8 text-lg font-semibold">{name}</p>
            <p className="text-sm text-white/70">
              {wallet.enrollmentDate ? `Member since ${wallet.enrollmentDate}` : wallet.programName}
            </p>
            <div className="mt-5 rounded-xl bg-white p-4 text-[#1a1a1a]">
              <p className="text-xs font-semibold tracking-wide text-[#595959]">
                POINTS BALANCE
              </p>
              <p className="text-3xl font-bold">
                {wallet.points.toLocaleString("en-IN")}
              </p>
              <p className="text-sm text-[#595959]">
                Worth {money(wallet.points * wallet.pointValue)} at checkout
                {wallet.pendingPoints > 0
                  ? ` · ${wallet.pendingPoints.toLocaleString("en-IN")} pending, not spendable yet`
                  : ""}
              </p>
            </div>
          </section>

          <section className="rounded-lg border border-[#d7efd9] bg-[#f3fbf4] p-4">
            <p className="text-xs font-semibold tracking-wide text-[#008009]">
              YOU HAVE SAVED
            </p>
            <p className="text-2xl font-bold text-[#1a1a1a]">
              {money(wallet.savedInr)}
            </p>
            <p className="text-sm text-[#595959]">
              Member rates and points on your trips.
            </p>
          </section>
        </div>

        <div className="space-y-4">
          <section className="rounded-lg border border-[#e7e7e7] bg-white p-5">
            <p className="text-sm font-semibold text-[#595959]">Current tier</p>
            <div className="mt-1 flex items-center justify-between gap-3">
              <h2 className="text-xl font-bold">{wallet.tierName}</h2>
              <span className="rounded-full bg-[#e8f6ea] px-3 py-1 text-xs font-semibold text-[#008009]">
                Active
              </span>
            </div>
            {wallet.nextTier ? (
              <>
                <p className="mt-3 text-sm">
                  {wallet.remaining.toLocaleString("en-IN")} more to reach{" "}
                  <span className="font-semibold">{wallet.nextTier}</span>.
                </p>
                <Progress className="mt-3 h-2" value={wallet.progressPct} />
                <div className="mt-2 flex justify-between text-xs text-[#595959]">
                  <span>
                    {wallet.tierName} · {money(wallet.qualifyingSpend)} spent
                  </span>
                  <span>{wallet.nextTier}</span>
                </div>
                <Link
                  to="/member"
                  className="mt-4 inline-flex h-10 items-center rounded-md bg-[#006ce4] px-4 text-sm font-semibold text-white"
                >
                  Book and move up
                </Link>
              </>
            ) : (
              <p className="mt-3 text-sm">You are on the highest tier.</p>
            )}
          </section>

          <section className="rounded-lg border border-[#e7e7e7] bg-white p-5">
            <h2 className="text-lg font-bold">{wallet.tierName} benefits</h2>
            <ul className="mt-3 space-y-2">
              {(wallet.benefits.length ? wallet.benefits : ["No extra perks on this tier yet."]).map((benefit) => (
                <li key={benefit} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#008009]" />
                  {benefit}
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-lg border border-[#e7e7e7] bg-white p-5">
            <h2 className="text-lg font-bold">All tiers</h2>
            {shownTiers.length === 0 ? (
              <p className="mt-3 text-sm text-[#595959]">This program has one membership level.</p>
            ) : null}
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {shownTiers.map((item) => {
                const current = item.name === wallet.tierName;
                return (
                  <div
                    key={item.name}
                    className={`rounded-md border p-3 text-sm ${
                      current ? "border-[#006ce4] bg-[#f3f8ff]" : "border-[#e7e7e7]"
                    }`}
                  >
                    <p className="font-bold">
                      {item.name}
                      {current ? " · You are here" : ""}
                    </p>
                    <p className="text-[#595959]">
                      {item.benefits[0] || "No extra perks listed"}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-lg border border-[#e7e7e7] bg-white px-5">
            <h2 className="pt-5 text-lg font-bold">Questions</h2>
            <Accordion type="single" collapsible>
              {FAQS.map((item) => (
                <AccordionItem key={item.q} value={item.q}>
                  <AccordionTrigger className="text-left text-sm">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#595959]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>
      </div>
    </PageWidth>
  );
}
